'use client'

import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts'
import { motion } from 'framer-motion'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { CardWatermark } from '@/components/ui/card-watermark'
import { Icons } from '@/components/ui/icons'

export interface ParameterBifurcationData {
  fatal_count: number
  non_fatal_count: number
  fatal_percentage: number
  non_fatal_percentage: number
  fatal_avg_score: number
  non_fatal_avg_score: number
}

interface GradeDistributionChartProps {
  data: ParameterBifurcationData
  qualityScore: number
  isLoading?: boolean
}

const SLICE_COLORS = {
  Fatal: '#ef4444',
  'Non-Fatal': '#10b981',
}

type ChartSlice = {
  name: string
  value: number
  count: number
  percentage: number
  avgScore: number
  color: string
  [key: string]: string | number
}

interface TooltipProps {
  active?: boolean
  payload?: Array<{
    payload: ChartSlice
  }>
}

function CustomTooltip({ active, payload }: TooltipProps) {
  if (!active || !payload?.length) return null
  const data = payload[0].payload

  return (
    <div className='rounded-xl border border-white/60 bg-white/95 p-3 shadow-float backdrop-blur-sm'>
      <div className='space-y-1'>
        <div className='flex items-center gap-2'>
          <div
            className='h-3 w-3 rounded-full'
            style={{ backgroundColor: data.color }}
          />
          <span className='font-semibold text-brand-navy'>{data.name}</span>
        </div>
        <p className='text-sm font-bold text-brand-navy'>
          {data.percentage}% · {data.count} call{data.count !== 1 ? 's' : ''}
        </p>
        <p className='text-xs text-muted-foreground'>
          Avg score: <span className='font-semibold text-brand-navy'>{data.avgScore}%</span>
        </p>
      </div>
    </div>
  )
}

interface LegendProps {
  payload?: Array<{
    value: string
    color: string
    payload: ChartSlice
  }>
}

function CustomLegend({ payload }: LegendProps) {
  if (!payload) return null

  return (
    <div className='mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2'>
      {payload.map((entry, index) => (
        <motion.div
          key={`legend-${index}`}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
          className='flex items-start gap-2'
        >
          <div
            className='mt-1 h-3 w-3 shrink-0 rounded-full'
            style={{ backgroundColor: entry.color }}
          />
          <div className='flex-1'>
            <span className='text-xs font-medium text-brand-navy'>{entry.value}</span>
            <p className='text-sm font-bold text-brand-navy'>{entry.payload.percentage}%</p>
            <p className='text-xs text-muted-foreground'>
              {entry.payload.count} call{entry.payload.count !== 1 ? 's' : ''} · avg{' '}
              {entry.payload.avgScore}%
            </p>
          </div>
        </motion.div>
      ))}
    </div>
  )
}

export function GradeDistributionChart({
  data,
  qualityScore,
  isLoading = false,
}: GradeDistributionChartProps) {
  const chartData: ChartSlice[] = [
    {
      name: 'Fatal',
      value: data.fatal_percentage,
      count: data.fatal_count,
      percentage: data.fatal_percentage,
      avgScore: data.fatal_avg_score,
      color: SLICE_COLORS.Fatal,
    },
    {
      name: 'Non-Fatal',
      value: data.non_fatal_percentage,
      count: data.non_fatal_count,
      percentage: data.non_fatal_percentage,
      avgScore: data.non_fatal_avg_score,
      color: SLICE_COLORS['Non-Fatal'],
    },
  ].filter((item) => item.count > 0)

  const hasData = chartData.length > 0

  if (isLoading) {
    return (
      <Card className='relative overflow-hidden'>
        <CardWatermark opacity={3} scale={1.1} />
        <CardHeader className='relative z-10'>
          <CardTitle className='flex items-center gap-2'>
            <Icons.activity className='h-5 w-5 text-brand-cornflower' strokeWidth={1.5} />
            Parameter Categorization
          </CardTitle>
        </CardHeader>
        <CardContent className='relative z-10'>
          <div className='flex h-[300px] items-center justify-center'>
            <Icons.loader className='h-8 w-8 animate-spin text-brand-cornflower' />
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className='relative overflow-hidden'>
      <CardWatermark opacity={3} scale={1.1} />
      <CardHeader className='relative z-10 pb-2'>
        <CardTitle className='flex items-center gap-2'>
          <Icons.activity className='h-5 w-5 text-brand-cornflower' strokeWidth={1.5} />
          Parameter Categorization
        </CardTitle>
        <p className='mt-1 text-xs text-muted-foreground'>
          Fatal vs non-fatal call distribution
        </p>
      </CardHeader>
      <CardContent className='relative z-10 pt-4'>
        {!hasData ? (
          <div className='flex h-[300px] flex-col items-center justify-center text-center'>
            <Icons.inbox className='mb-3 h-10 w-10 text-gray-200' />
            <p className='text-sm text-muted-foreground'>
              No analyzed calls yet
            </p>
          </div>
        ) : (
          <div className='relative'>
            <ResponsiveContainer width='100%' height={280}>
              <PieChart>
                <Pie
                  data={chartData}
                  cx='50%'
                  cy='50%'
                  innerRadius='60%'
                  outerRadius='80%'
                  paddingAngle={2}
                  dataKey='value'
                  animationBegin={0}
                  animationDuration={800}
                  animationEasing='ease-out'
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend content={<CustomLegend />} />
              </PieChart>
            </ResponsiveContainer>

            <div className='pointer-events-none absolute inset-0 flex items-center justify-center'>
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.3, type: 'spring', stiffness: 200 }}
                className='text-center'
                style={{ marginBottom: '80px' }}
              >
                <div className='text-4xl font-bold text-brand-navy'>
                  {qualityScore}%
                </div>
                <div className='text-xs text-muted-foreground'>Quality Score</div>
              </motion.div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
